import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Globe,
  Users,
  ExternalLink,
  FileText,
  Building2,
  MapPin,
  Image as ImageIcon,
  Settings,
  LogOut,
  Sparkles,
  Library,
  Star,
  ShieldCheck,
  Wand2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const leadGenNav = [
  { path: "/admin", icon: LayoutDashboard, label: "Overview", exact: true },
  { path: "/admin/builder", icon: Building2, label: "Website Builder" },
  { path: "/admin/projects", icon: FileText, label: "My Projects" },
  { path: "/admin/websites", icon: Globe, label: "Websites" },
  { path: "/admin/leads", icon: Users, label: "Leads" },
  { path: "/admin/zip-lookup", icon: MapPin, label: "ZIP Lookup" },
  { path: "/admin/logo-generator", icon: ImageIcon, label: "Logo Generator" },
];

const affiliateNav = [
  { path: "/admin/affiliate/sites", icon: Sparkles, label: "My Sites" },
  { path: "/admin/affiliate/tools", icon: Library, label: "Tools Library" },
];

const commercialNav = [
  { path: "/admin/commercial", icon: Building2, label: "A-Star Dashboard" },
];

const geoNav = [
  { path: "/admin/geo", icon: Star, label: "GEO Restoration" },
];

const reputationNav = [
  { path: "/admin/reputation", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { path: "/admin/reputation/leads", icon: Users, label: "Leads" },
  { path: "/admin/reputation/ai", icon: Wand2, label: "AI Generator" },
  { path: "/admin/reputation/publish", icon: Globe, label: "WordPress Publish" },
];

const NavLinkItem = ({ item, location }) => {
  const isActive = item.exact
    ? location.pathname === item.path
    : location.pathname.startsWith(item.path);
  return (
    <li key={item.path}>
      <Link
        to={item.path}
        className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
          isActive
            ? "bg-orange-500 text-white"
            : "text-slate-400 hover:bg-slate-800 hover:text-white"
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <item.icon className="w-5 h-5" />
        <span className="font-medium">{item.label}</span>
      </Link>
    </li>
  );
};

export const Sidebar = ({ onLogout }) => {
  const location = useLocation();

  return (
    <aside
      className="w-64 bg-slate-900 min-h-screen flex flex-col"
      data-testid="admin-sidebar"
    >
      <div className="p-6 border-b border-slate-800">
        <h1
          className="text-white font-bold text-lg"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          LeadGen Builder
        </h1>
        <p className="text-slate-400 text-sm mt-1">Website Generator</p>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <div className="text-[10px] tracking-wider uppercase text-slate-500 px-4 mb-2">
          Lead Gen
        </div>
        <ul className="space-y-1.5">
          {leadGenNav.map((item) => (
            <NavLinkItem key={item.path} item={item} location={location} />
          ))}
        </ul>

        <div className="mt-6 text-[10px] tracking-wider uppercase text-orange-400/80 px-4 mb-2 flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Affiliate Hub
        </div>
        <ul className="space-y-1.5" data-testid="sidebar-affiliate-section">
          {affiliateNav.map((item) => (
            <NavLinkItem key={item.path} item={item} location={location} />
          ))}
        </ul>

        <div className="mt-6 text-[10px] tracking-wider uppercase text-amber-500 px-4 mb-2 flex items-center gap-1">
          <Building2 className="w-3 h-3" /> Commercial Network
        </div>
        <ul className="space-y-1.5" data-testid="sidebar-commercial-section">
          {commercialNav.map((item) => (
            <NavLinkItem key={item.path} item={item} location={location} />
          ))}
        </ul>

        <div className="mt-6 text-[10px] tracking-wider uppercase text-orange-500 px-4 mb-2 flex items-center gap-1">
          <Star className="w-3 h-3" /> A-Star Restoration
        </div>
        <ul className="space-y-1.5" data-testid="sidebar-geo-section">
          {geoNav.map((item) => (
            <NavLinkItem key={item.path} item={item} location={location} />
          ))}
        </ul>

        <div className="mt-6 text-[10px] tracking-wider uppercase text-rose-400 px-4 mb-2 flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> Reputation Recovery
        </div>
        <ul className="space-y-1.5" data-testid="sidebar-reputation-section">
          {reputationNav.map((item) => (
            <NavLinkItem key={item.path} item={item} location={location} />
          ))}
        </ul>

        <div className="mt-6 border-t border-slate-800 pt-4">
          <ul className="space-y-1.5">
            <NavLinkItem
              item={{ path: "/admin/settings", icon: Settings, label: "Settings" }}
              location={location}
            />
          </ul>
        </div>
      </nav>

      <div className="p-4 border-t border-slate-800 space-y-3">
        <a
          href="/landing.html"
          className="flex items-center space-x-2 text-slate-400 hover:text-white text-sm"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="view-landing-link"
        >
          <ExternalLink className="w-4 h-4" />
          <span>View Demo Site</span>
        </a>
        {onLogout && (
          <button
            onClick={onLogout}
            className="flex items-center space-x-2 text-slate-500 hover:text-red-400 text-sm w-full transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        )}
      </div>
    </aside>
  );
};

export { API };
